import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Switch, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Input from '~/components/Input';
import InputSelect from '~/components/InputSelect';
import { useContractLoader } from '~/hooks/useContractLoader';
import { ContractPostEmergency } from '~/services/modules/contract/types';
import useStore, { storeState } from '~/store';
import { getErrorMessage } from '~/utils/errorHandler';
import schema from '../../step5/validation';
import { CONTRACT_JOURNEY_APP_IDS, contractStepField } from '../contractAppIds';
import { contractPath } from '../buildContractSearchParams';
import ContractJourneyShell from '../components/ContractJourneyShell';
import ContractStepCard from '../components/ContractStepCard';

const securityQuestions = [
  { value: 1, label: 'Nome do primeiro cachorro' },
  { value: 2, label: 'time que torce' },
  { value: 3, label: 'Nome da primeira professora' }
];

export default function ContractStep3Page() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [callThePolice, setCallThePolice] = useState(false);

  const { contract_id } = useContractLoader({ stepName: 'contract_journey_step3' });

  const { contractAsyncPostEmergency } = useStore((store) => ({
    contractAsyncPostEmergency: store.contractAsyncPostEmergency
  }));

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields }
  } = useForm<ContractPostEmergency>({
    resolver: yupResolver(schema)
  });

  const onSubmit: SubmitHandler<ContractPostEmergency> = async (data) => {
    if (!contract_id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const orgParam = searchParams.get('org');

    try {
      await contractAsyncPostEmergency(
        contract_id,
        {
          name: data.name.trim(),
          security_question: +data.security_question,
          security_answer: data.security_answer.trim(),
          mobile_phone: data.mobile_phone,
          call_the_police: callThePolice
        },
        orgParam ?? undefined
      );
      navigate(contractPath('/contract/download', searchParams));
    } catch (error: unknown) {
      storeState.addToast(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContractJourneyShell
      appId={CONTRACT_JOURNEY_APP_IDS.step3}
      onBack={() => navigate(contractPath('/contract/equipment', searchParams))}
      progressPercent={89}
    >
      <form data-app-id={`${CONTRACT_JOURNEY_APP_IDS.step3}-form`} onSubmit={handleSubmit(onSubmit)}>
        <ContractStepCard
          actionsAppIdPrefix={CONTRACT_JOURNEY_APP_IDS.step3}
          loading={loading}
          onPrimary={() => {}}
          primaryLabel="Finalizar"
          stepLabel="Passo 3 de 3"
          title="Dados de emergência"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(3, 'contact-name')}
                  label="Contato"
                  placeholder="Nome completo"
                  error={Boolean(errors.name)}
                  isDirty={dirtyFields.name}
                  helperText={errors.name?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name="mobile_phone"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(3, 'mobile-phone')}
                  label="Celular"
                  mask="phone"
                  placeholder="(DDD) 9 9999-9999"
                  error={Boolean(errors.mobile_phone)}
                  isDirty={dirtyFields.mobile_phone}
                  helperText={errors.mobile_phone?.message}
                  {...field}
                />
              )}
            />
            <Box
              data-app-id={contractStepField(3, 'call-police')}
              sx={{
                alignItems: 'flex-start',
                bgcolor: theme.customColors.backgroundMute,
                borderRadius: 1,
                display: 'flex',
                gap: 1,
                justifyContent: 'space-between',
                p: 2
              }}
            >
              <Typography variant="body1">
                Dependendo da localização da ocorrência, podemos acionar a polícia para assistência.
              </Typography>
              <Switch
                checked={callThePolice}
                data-app-id={contractStepField(3, 'call-police-switch')}
                onChange={() => {
                  const next = !callThePolice;
                  setCallThePolice(next);
                  setValue('call_the_police', next);
                }}
              />
            </Box>

            <Typography variant="h4">Dados para atendimento</Typography>

            <InputSelect
              appId={contractStepField(3, 'security-question')}
              error={Boolean(errors.security_question)}
              helperText={errors.security_question?.message}
              items={securityQuestions}
              label="Pergunta de segurança"
              name="security_question"
              placeholder="Escolha a pergunta"
              setValue={(value) => setValue('security_question', +value)}
            />
            <Controller
              name="security_answer"
              control={control}
              render={({ field }) => (
                <Input
                  appId={contractStepField(3, 'security-answer')}
                  label="Responda a pergunta"
                  placeholder="Sua resposta"
                  error={Boolean(errors.security_answer)}
                  isDirty={dirtyFields.security_answer}
                  helperText={errors.security_answer?.message}
                  {...field}
                />
              )}
            />
          </Box>
        </ContractStepCard>
      </form>
    </ContractJourneyShell>
  );
}
